import './styles/SubNavigation.scss';

import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';

import eventBus from '../../helpers/eventBus';
import { useClassHelper, useComponentWidth } from '../../hooks';
import { SubRoute } from '../../types';

const componentName = 'c-SubNavigation';

const CREATE_EVENT = 'sub-navigation-create';
const DESTROY_EVENT = 'sub-navigation-destroy';
const ROUTE_KEY = 'sub-route';

interface Options {
  header?: ReactNode;
}

type Routes<K extends string, T> = Record<K, T>;

interface ICreateData {
  routes: Routes<string, SubRoute>;
  options?: Options;
}

/**
 * A navigation component that subscribes to events to dynamically update its routes.
 * Uses an event bus to handle lifecycle events like initialization and destruction.
 */
export function SubNavigation() {
  const [{ routes, options }, setState] = useState<ICreateData>({ routes: {} });
  const { ref } = useComponentWidth('sub-navigation-width');
  const c = useClassHelper(componentName);
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);

  const routesList = useMemo(() => {
    return Object.keys(routes).map(routeKey => ({
      ...routes[routeKey],
    }));
  }, [routes]);

  useEffect(() => {
    /**
     * Handles the "init" event by setting routes.
     * @param data Contains the routes to be set, expected to have a 'routes' property.
     */
    const initHandler = ({ routes, options }: ICreateData) => {
      setState({ routes: routes, options: options });
    };

    /**
     * Handles the "destroy" event by clearing the routes.
     */
    const destroyHandler = () => {
      setState({ routes: {} });
    };

    eventBus.subscribe(CREATE_EVENT, initHandler);
    eventBus.subscribe(DESTROY_EVENT, destroyHandler);

    return () => {
      eventBus.unsubscribe(CREATE_EVENT, initHandler);
      eventBus.unsubscribe(DESTROY_EVENT, destroyHandler);
    };
  }, []);

  return (
    <section className={c('base')} ref={ref}>
      <header>{options?.header}</header>
      <ul>
        {routesList.map(route => (
          <li
            key={route.slug}
            className={c({
              '--active': searchParams.get(ROUTE_KEY) === route.slug,
            })}
          >
            <Link
              to={{
                pathname: location.pathname,
                search: new URLSearchParams({
                  [ROUTE_KEY]: route.slug,
                }).toString(),
              }}
            >
              <span className="icon">{route.icon}</span>
              <span className="title">{route.name}</span>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}

const create = (createData: ICreateData) => {
  eventBus.publish(CREATE_EVENT, createData);
};

const destroy = () => {
  eventBus.publish(DESTROY_EVENT, {});
};

interface SubNavigationEventBusType {
  create: (createData: ICreateData) => void;
  destroy: () => void;
  constants: {
    CREATE_EVENT: string;
    DESTROY_EVENT: string;
  };
}

export const SubNavigationEventBus: SubNavigationEventBusType = {
  create,
  destroy,
  constants: {
    DESTROY_EVENT,
    CREATE_EVENT,
  },
};

export const useSubNavigation = (prevRoutes: Record<string, SubRoute>) => {
  const location = useLocation();
  const navigate = useNavigate();

  const searchParams = new URLSearchParams(location.search);
  const currentRoute = searchParams.get(ROUTE_KEY);
  const [currentRouteKey, setCurrentRouteKey] = useState(null);
  const [routes, setRoutes] = useState(prevRoutes);

  const handleNavigate = (route: { slug: string }) => {
    if (!route) {
      return;
    }

    const searchParams = new URLSearchParams({
      [ROUTE_KEY]: route.slug,
    });

    navigate(`${location.pathname}?${searchParams.toString()}`);
  };

  const findRouteBySlug = useCallback(
    (slug: string): string | undefined => {
      for (const [routeKey, route] of Object.entries(routes)) {
        if (route.slug === slug) {
          return routeKey;
        }
      }
      return undefined;
    },
    [routes],
  );

  const updateRoutes = (newRoutes: Record<string, SubRoute>) => {
    setRoutes(newRoutes);
  };

  const goForwardSubNavigation = useCallback(() => {
    const currentRouteIndex = Object.keys(routes).indexOf(currentRouteKey);
    const nextRoute = Object.keys(routes)[currentRouteIndex + 1];
    if (nextRoute) {
      handleNavigate(routes[nextRoute]);
    }
  }, [currentRouteKey, routes]);

  useEffect(() => {
    if (!currentRoute) {
      handleNavigate(routes[Object.keys(routes)[0]]);
      return;
    }
    const routeKey = findRouteBySlug(currentRoute);

    if (routeKey) {
      setCurrentRouteKey(routeKey);
    }
  }, [currentRoute]);

  const resetRoute = useCallback(
    filteredRoutes => {
      handleNavigate(filteredRoutes[Object.keys(filteredRoutes)[0]]);
    },
    [routes],
  );

  return {
    currentRoute,
    currentRouteKey,
    updateRoutes,
    goForwardSubNavigation,
    resetRoute,
  };
};
